/**
 * Groups API request/response DTOs (backend shapes).
 */
import type { ProblemDetails } from '../apiTypes';

/** Backend group role IDs (documented in API_Queries response). */
export const GROUP_ROLE_ID = {
  Owner: 1,
  Admin: 2,
  Member: 3,
} as const;

export type GroupRoleId = (typeof GROUP_ROLE_ID)[keyof typeof GROUP_ROLE_ID];

export type ApiGroupSortBy =
  | 'Newest'
  | 'Oldest'
  | 'MostMembers'
  | 'AToZ'
  | 'ZToA';

export interface GetGroupsQueryParams {
  SearchTerm?: string;
  PageNumber?: number;
  PageSize?: number;
  SortBy?: ApiGroupSortBy;
}

export interface ApiGroupListItem {
  groupId: number;
  ownerUserId?: number;
  name: string;
  description?: string;
  iconUrl?: string;
  bannerUrl?: string;
  isPrivate?: boolean;
  requireJoinApproval?: boolean;
  requirePostApproval?: boolean;
  onlyAdminCanPost?: boolean;
  maxMembers?: number;
  memberCount?: number;
  createdAt?: string;
  /** @deprecated Prefer memberStatus */
  membershipStatus?: string;
  memberStatus?: 'notMember' | 'joined' | 'pending' | string;
  isOwner?: boolean;
  isAdmin?: boolean;
}

export interface ApiPaginatedGroups {
  items: ApiGroupListItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  message?: string;
}

export interface ApiMyGroupItem {
  groupId: number;
  name: string;
  description?: string;
  iconUrl?: string;
  bannerUrl?: string;
  isPrivate?: boolean;
  memberCount?: number;
  createdAt?: string;
  /** @deprecated Prefer memberStatus */
  membershipStatus?: string;
  memberStatus?: 'notMember' | 'joined' | 'pending' | string;
  isOwner?: boolean;
  isAdmin?: boolean;
}

export interface ApiGroupMutationEnvelope<T = ApiGroupRecord> {
  isSuccess?: boolean;
  success?: boolean;
  message?: string;
  errors?: string[];
  data?: T;
}

export interface ApiGroupRecord {
  groupId: number;
  ownerUserId?: number;
  name: string;
  description?: string;
  iconUrl?: string;
  bannerUrl?: string;
  isPrivate?: boolean;
  requireJoinApproval?: boolean;
  requirePostApproval?: boolean;
  onlyAdminCanPost?: boolean;
  maxMembers?: number;
}

export interface CreateGroupRequest {
  name: string;
  description: string;
  iconUrl?: string;
  bannerUrl?: string;
  isPrivate: boolean;
  requireJoinApproval: boolean;
  requirePostApproval: boolean;
  onlyAdminCanPost: boolean;
  maxMembers?: number;
}

export interface UpdateGroupRequest {
  groupId: number;
  name: string;
  description: string;
  iconUrl?: string;
  bannerUrl?: string;
  requireJoinApproval?: boolean;
  isPrivate?: boolean;
  requirePostApproval?: boolean;
  onlyAdminCanPost?: boolean;
}

export interface GroupIdRequest {
  groupId: number;
}

export interface AddMemberRequest {
  groupId: number;
  userId: number;
  roleId: GroupRoleId;
}

export interface ChangeMemberRoleRequest {
  groupId: number;
  userId: number;
  newRoleId: GroupRoleId;
}

export interface RemoveMemberRequest {
  groupId: number;
  userId: number;
}

export type JoinRequestReviewAction = 'Approve' | 'Reject' | string;

export interface ReviewJoinRequestPayload {
  groupJoinRequestId: number;
  action: JoinRequestReviewAction;
  /** Optional admin note when approving or rejecting. */
  reviewComments?: string;
}

export interface GetGroupJoinRequestsPayload {
  groupId: number;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: string;
}

export interface ApiGroupJoinRequestItem {
  groupJoinRequestId: number;
  groupId: number;
  requestedByUserId: number;
  userGuid?: string;
  profileId?: number;
  firstName?: string;
  userName?: string;
  profileImage?: string;
  requestedAt?: string;
}

export interface ApiPaginatedJoinRequests {
  items: ApiGroupJoinRequestItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  message?: string;
}

export interface ApiJoinRequestsEnvelope {
  isSuccess?: boolean;
  message?: string;
  errors?: string[];
  data?: ApiPaginatedJoinRequests;
}

export interface ApiGroupDetailRecord {
  groupId: number;
  groupName?: string;
  name?: string;
  ownerId?: number;
  ownerUserId?: number;
  ownerUserName?: string;
  ownerPhoto?: string;
  /** Backend DetailResponse documents this as int32 (0/1); some payloads use boolean. */
  isOwner?: boolean | number;
  isAdmin?: boolean;
  description?: string;
  iconUrl?: string;
  bannerUrl?: string;
  isPrivate?: boolean;
  requireJoinApproval?: boolean;
  requirePostApproval?: boolean;
  onlyAdminCanPost?: boolean;
  isArchived?: boolean;
  createdAt?: string;
  membershipStatus?: string;
  currentUserMembershipStatus?: string;
  memberStatus?: 'notMember' | 'joined' | 'pending' | string;
  memberCount?: number;
}

export interface ApiGroupMemberRecord {
  groupId?: number;
  memberId?: number;
  userId?: number;
  /** UUID used for View Profile navigation. */
  userGuid?: string;
  profileId?: number;
  firstName?: string;
  userName?: string;
  userProfile?: string;
  profileImage?: string;
  groupRole?: string;
  createdAt?: string;
}

export interface ApiGroupActivityRecord {
  groupId?: number;
  membersCount?: number;
  newJoinedCount?: number;
  newJoinRequestCount?: number;
  newPostCount?: number;
}

export interface ApiFeedItem {
  postId: number;
  groupId?: number;
  userId?: number;
  profileUserGuid?: string | null;
  userName?: string;
  firstName?: string;
  groupName?: string;
  userPhoto?: string;
  postDate?: string;
  commentCount?: number;
  reactedCount?: number;
  isReacted?: boolean | number;
  totalShares?: number;
  bodyText?: string;
  title?: string;
  url?: string;
  contentTypeName?: string;
  photoAlbum?: string[];
  thumbnailUrl?: string;
  currentWeight?: number;
  scaleUnitWeightName?: string;
  allowShare?: boolean;
}

export interface ApiGetGroupDetailResponse {
  statusCode?: number;
  message?: string;
  success?: boolean;
  groupDetail?: ApiGroupDetailRecord | ApiGroupDetailRecord[];
  groupMembers?: ApiGroupMemberRecord[];
  groupActivity?: ApiGroupActivityRecord | ApiGroupActivityRecord[];
  feeds?: ApiFeedItem[];
  membershipStatus?: string;
}

export interface GetGroupsListRequest {
  isMyGroup: boolean;
  searchTerm?: string;
}

export interface ApiGetGroupsListResponse {
  statusCode?: number;
  message?: string;
  success?: boolean;
  groups?: Array<{
    groupId: number;
    groupName: string;
    iconUrl?: string;
    membersCount?: number;
    isJoined?: boolean;
  }>;
}

export interface GetFeedsRequest {
  method: 'Groups' | string;
  startIndex?: number;
  pageSize?: number;
  search?: string;
  isMyGroup?: boolean;
  timezoneOffset?: number;
}

export interface ApiGetFeedsResponse {
  statusCode?: number;
  message?: string;
  success?: boolean;
  feeds?: ApiFeedItem[];
}

export interface ApiActionResponse {
  success?: boolean;
  status?: string;
  message?: string;
}

export type GroupsApiError = ProblemDetails | ApiGroupMutationEnvelope<unknown>;
